import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Helmet } from "react-helmet";
import { useParams, useLocation } from "react-router-dom";
import SearchDropdown from "../../../../../../search/seatchDropdown";

function EditIntegration({ chimeraserv, user, data }) {
  const location = useLocation();
  const { id } = useParams();
  const [groups, setGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [form, setForm] = useState({
    group: { value: "", state: null },
    brand: { value: "", state: null },
    name: { value: "", state: null },
    vendorCountry: { value: "", state: null },
    vendorID: { value: "", state: null },
    vendorName: { value: "", state: null },
    message: { value: "", state: null },
    state: false,
  });
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [outletStores, setOutletStores] = useState([]);
  const [integrationData, setIntegrationData] = useState(
    location.state?.data || null
  );
  const groupDropdownRef = useRef(null);

  const headers = {
    Authorization: user.data.usrKey,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Fetch integration data for editing
  // useEffect(() => {
  //   const fetchIntegration = async () => {
  //     try {
  //       const response = await axios.get(
  //         chimeraserv.urlGet + "/integration/view/" + id,
  //         { headers }
  //       );
  //       setIntegrationData(response.data);
  //     } catch (error) {
  //       setIntegrationData(null);
  //     }
  //   };
  //   if (id) fetchIntegration();
  // }, [id, chimeraserv.urlGet, user.data.usrKey]);

  // Fetch groups and brands
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axios.get(chimeraserv.urlGet + "/group", {
          headers,
        });
        setGroups(response.data);
        setFilteredGroups(response.data);
      } catch (error) {
        setGroups([]);
        setFilteredGroups([]);
      }
    };
    fetchGroups();
  }, [chimeraserv.urlGet, user.data.usrKey]);

  // Prefill form when integrationData is loaded
  useEffect(() => {
    if (integrationData) {
      const groupObj = groups.find((g) => g.grpKey === integrationData.grpKey);
      setSelectedGroup(groupObj || null);
      setGroupSearchTerm(groupObj ? groupObj.name : "");
      setBrands(groupObj ? groupObj.brands || [] : []);
      setForm((prev) => ({
        ...prev,
        group: { value: integrationData.grpKey, state: true },
        brand: { value: integrationData.braKey, state: true },
        name: { value: integrationData.name, state: true },
        vendorCountry: {
          value: integrationData.vendorCountry || "",
          state: true,
        },
        vendorID: { value: integrationData.vendorID || "", state: true },
        vendorName: { value: integrationData.vendorName || "", state: true },
        message: { value: "", state: null },
      }));
      setOutletStores(
        (integrationData.outlets || []).map((o) => ({
          chaKey: o.chaKey,
          storeNum: o.storeNum,
        }))
      );
    }
  }, [integrationData, groups]);

  // Update outlets when brand changes
  useEffect(() => {
    if (!form.brand.value) {
      setOutlets([]);
      return;
    }
    const brandObj = brands.find((b) => b.braKey === form.brand.value);
    if (brandObj && brandObj.chains) {
      setOutlets(brandObj.chains);
    } else {
      setOutlets([]);
    }
  }, [form.brand.value, brands]);

  const checker = (state) => {
    const check = { icon: "check-circle", style: "null" };
    switch (state) {
      case true:
        check.icon = "check-circle";
        check.style = "correct";
        break;
      case false:
        check.icon = "times-circle";
        check.style = "wrong";
        break;
      default:
        break;
    }
    return check;
  };

  // Only allow adding outlets not already present
  const availableOutlets = outlets.filter(
    (o) => !outletStores.some((item) => item.chaKey === o.chaKey)
  );

  const handleOutletChange = (idx, value) => {
    setOutletStores((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, chaKey: value } : item))
    );
  };

  const handleStoreNumChange = (idx, value) => {
    setOutletStores((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, storeNum: value } : item))
    );
  };

  const handleAddOutlet = () => {
    if (availableOutlets.length > 0) {
      setOutletStores((prev) => [...prev, { chaKey: "", storeNum: "" }]);
    }
  };

  const handleRemoveOutlet = (idx) => {
    setOutletStores((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: { value, state: true },
      message: { value: "", state: null },
    }));
  };

  const handleReset = () => {
    if (integrationData) {
      setForm({
        group: { value: integrationData.grpKey, state: true },
        brand: { value: integrationData.braKey, state: true },
        name: { value: integrationData.name, state: true },
        vendorCountry: {
          value: integrationData.vendorCountry || "",
          state: true,
        },
        vendorID: { value: integrationData.vendorID || "", state: true },
        vendorName: { value: integrationData.vendorName || "", state: true },
        message: { value: "", state: null },
        state: false,
      });
      setOutletStores(
        (integrationData.outlets || []).map((o) => ({
          chaKey: o.chaKey,
          storeNum: o.storeNum,
        }))
      );
    }
  };

  const isFormValid =
    form.group.value &&
    form.brand.value &&
    form.name.value &&
    form.vendorCountry.value &&
    form.vendorID.value &&
    form.vendorName.value &&
    outletStores.every((item) => item.chaKey && item.storeNum);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    try {
      const outletsPayload = outletStores.map((item) => ({
        chaKey: item.chaKey,
        storeNum: item.storeNum,
      }));

      const EditResponse = await axios({
        method: "PUT",
        url: chimeraserv.urlPost + "/integration/edit/" + id,
        headers: {
          Authorization: user.data.usrKey,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        data: {
          grpKey: form.group.value,
          braKey: form.brand.value,
          name: form.name.value,
          vendorID: form.vendorID.value,
          vendorName: form.vendorName.value,
          vendorCountry: form.vendorCountry.value,
          outlets: outletsPayload,
        },
      });

      setForm((prev) => ({
        ...prev,
        message: { value: "Integration Updated Successfully!", state: true },
      }));
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        message: { value: "Error updating integration", state: false },
        state: false,
      }));
    }
  };

  return (
    <div className="custForm">
      <Helmet>
        <title>Edit Integration | Chimera Solutions | Insights</title>
      </Helmet>
      <div className="viewTitle">Edit Integration</div>
      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>
            Name <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.name.state).style].join(" ")}
            icon={["fas", checker(form.name.state).icon]}
          />
          <input
            type="text"
            className="form-control"
            name="name"
            value={form.name.value ?? ""}
            onChange={handleChange}
            placeholder="Enter name"
          />
        </div>
        <div className="form-group">
          <label>
            VendorID <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.vendorID.state).style].join(" ")}
            icon={["fas", checker(form.vendorID.state).icon]}
          />
          <input
            type="text"
            className="form-control"
            name="vendorID"
            value={form.vendorID.value ?? ""}
            onChange={handleChange}
            placeholder="Enter VendorID"
          />
        </div>
        <div className="form-group">
          <label>
            Vendor Name <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.vendorName.state).style].join(" ")}
            icon={["fas", checker(form.vendorName.state).icon]}
          />
          <input
            type="text"
            className="form-control"
            name="vendorName"
            value={form.vendorName.value ?? ""}
            onChange={handleChange}
            placeholder="Enter Vendor Name"
          />
        </div>
        <div className="form-group">
          <label>
            Vendor Country <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.vendorCountry.state).style].join(
              " "
            )}
            icon={["fas", checker(form.vendorCountry.state).icon]}
          />
          <input
            type="text"
            className="form-control"
            name="vendorCountry"
            value={form.vendorCountry.value ?? ""}
            onChange={handleChange}
            placeholder="Enter Vendor Country"
          />
        </div>
        <div className="form-group" ref={groupDropdownRef}>
          <label>
            Group <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.group.state).style].join(" ")}
            icon={["fas", checker(form.group.state).icon]}
          />
          <SearchDropdown
            data={filteredGroups}
            name="group"
            value={groupSearchTerm}
            searchTerm={groupSearchTerm}
            searchFields={["name", "grpKey"]}
            displayField="name"
            placeholder="Search business group..."
            required={true}
            showIcon={false}
            state={!!selectedGroup}
            onChange={() => {}}
            onSelect={() => {}}
            allowClear={false}
            noResultsText="No groups found"
            disabled={true}
          />
        </div>
        <div className="form-group">
          <label>
            Brand <span>*</span>
          </label>
          <FontAwesomeIcon
            className={["icon", checker(form.brand.state).style].join(" ")}
            icon={["fas", checker(form.brand.state).icon]}
          />
          <select
            className="form-control"
            name="brand"
            value={form.brand.value}
            onChange={() => {}}
            disabled={true}
          >
            <option value="">-- Select Brand --</option>
            {brands.map((b, key) => (
              <option value={b.braKey} key={key}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Outlets & Store Numbers</label>
          <div className="outlet-list">
            {outletStores.map((item, idx) => (
              <div className="outlet-row" key={idx}>
                <select
                  className="form-control"
                  value={item.chaKey}
                  onChange={(e) => handleOutletChange(idx, e.target.value)}
                  disabled={!outlets.length}
                >
                  <option value="">-- Select Outlet --</option>
                  {outlets.map((o) => (
                    <option value={o.chaKey} key={o.chaKey}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  value={item.storeNum ?? ""}
                  onChange={(e) => handleStoreNumChange(idx, e.target.value)}
                  placeholder="Store Number"
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleRemoveOutlet(idx)}
                  style={{ minWidth: "2em" }}
                  disabled={outletStores.length === 1}
                >
                  &times;
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddOutlet}
              style={{ marginTop: "1em", width: "100%" }}
              disabled={availableOutlets.length === 0}
            >
              + ADD
            </button>
          </div>
        </div>
        {form.message.value && (
          <div className="message form-group">{form.message.value}</div>
        )}
        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!isFormValid}
          >
            Update
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-primary reset"
          >
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditIntegration;
